<template>
  <div>
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
    ></loading>
    <v-wait for="settings">
      <div class="video-internal-wrapper vim-css" :style="[customPlayButton]">
        <video
          id="videojs-event-tracking-player"
          ref="videoPlayer"
          class="vjs-fluid"
          controls
          data-matomo-ignore
        ></video>

        <audio
          id="groovevideo-audio-player"
          ref="audioPlayer"
          preload="auto"
          allow="autoplay"
          v-if="audio && audio.src"
        >
          <source id="main-audio-track" :src="audio.src" :type="audio.type" />
        </audio>
      </div>
    </v-wait>
  </div>
</template>

<script>
import videoFormatMixin from '@/player/formatMixin';
import videojs from 'video.js';
import 'videojs-watermark';
import 'video.js/dist/video-js.css';
import 'videojs-overlay/dist/videojs-overlay.js';
import 'videojs-overlay/dist/videojs-overlay.css';
import 'videojs-contrib-quality-levels';

import '@cmarasani/videojs-hls-quality-selector';
import '@groovepages/gd-ui-kit/components/video-player/assets/css/youtube.css';
import '@groovepages/gd-ui-kit/components/video-player/assets/css/vim.css';
import '@groovepages/gd-ui-kit/components/video-player/assets/css/wistia.css';
import '@groovepages/gd-ui-kit/components/video-player/assets/css/videoPlayer.css';
import '@groovepages/gd-ui-kit/components/video-player/assets/css/videoPlayer.scss';
import videoMixin from '@groovepages/gd-ui-kit/components/video-player/assets/utils/videoSourceMixin';
import videoHelper from '@groovepages/gd-ui-kit/components/video-player/assets/mixins/videoHelper';
import youtubeSourcePlugin from '@groovepages/gd-ui-kit/components/video-player/assets/plugins/youtubeSourcePlugin';
import Loading from 'vue-loading-overlay';
import axios from 'axios';
import Cookies from 'js-cookie';
import Video from '@/models/Video';
import themeConfig from '@/theme/themeConfig.js';
import 'videojs-event-tracking/dist/videojs-event-tracking.js';
import router from '@/router';
import SeekButton from '@/assets/utils/SeekButton';
import { usesModernAutoplay } from './editor/player/newAppearance';
import { hlsPreload } from '@/utils/muxSource';

const videoStatus = '';
// import 'videojs-playlist/dist/videojs-playlist.js'
// import 'videojs-playlist/src/auto-advance.js'

// Helper function to extract YouTube video ID from various URL formats
function extractYoutubeVideoId(url) {
  if (!url) return null;
  
  // Match patterns for standard, shortened, and embed URLs
  const patterns = [
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i,
    /^[^"&?\/\s]{11}$/  // Direct video ID
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
}

// Helper function to get high-quality YouTube thumbnail URL
async function getYoutubeThumbnailUrl(videoId) {
  if (!videoId) return null;
  
  // Try maxresdefault.jpg first
  const maxResUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  try {
    const response = await fetch(maxResUrl);
    if (response.ok) {
      return maxResUrl;
    }
  } catch (error) {
    console.warn('Failed to fetch maxresdefault thumbnail:', error);
  }
  
  // Fall back to hqdefault.jpg
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}


export default {
  name: 'VideoPlayer',
  components: {
    Loading,
  },
  mixins: [videoFormatMixin, videoMixin, videoHelper],
  props: {
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    // activeIntegrations: {
    //   default: true,
    // }
  },
  data() {
    return {
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      overlayText: '',
      player: null,
      vjsClass: 'video-js vjs-big-play-centered vjs-skin-aqua',
      vid: [],
      localModel: this.model,
      cta_form_name: {
        content: '',
        start: 1,
        end: 100,
        align: 'right',
      },
      cta_form_email: {
        content: '',
        start: 1,
        end: 100,
        align: 'right',
      },
      cta_form_submit: {
        content: '',
        start: 1,
        end: 100,
        align: 'right',
      },
      ctaBanner: {
        content: '',
        start: 1,
        end: 100,
        align: 'left',
      },
      cta_caption: {
        content: '',
        start: 1,
        end: 100,
        align: 'bottom-left',
      },
      cta_caption_button: {
        content: '',
        start: 1,
        end: 100,
        align: 'bottom-left',
      },
      ctaSkip: {
        content: '',
        start: 1,
        end: 100,
        align: 'bottom',
      },
      cta_fb_social_share: {
        content: '',
        start: 1,
        end: 100,
        align: 'right',
      },
      cta_tw_social_share: {
        content: '',
        start: 1,
        end: 100,
        align: 'right',
      },
      cta_ask_share_link: {
        content: '',
        start: 1,
        end: 100,
        align: 'right',
      },
      cta_share_link_button: {
        content: '',
        start: 1,
        end: 100,
        align: 'right',
      },
      cta_form_init: true,
      cta_form: [],
      v_length: '',
      playVideo: 'pause',
      dimensions_type: 1,
      video_width: 1280,
      video_height: 720,
      video_permalink: '',
      showAnimation: false,
      showUnmuteImage: false,
      hidePlayButton: false,
      optinDetails: [],
      watchedUntilPoints: [],
      triggerWatchedUntil: false,
      videoEvents: null,
      visibilityRefresh: null,
      audioPlayer: null,
      audio: {
        src: null,
        type: 'audio/mp4',
      },
      overlayTimings: [],
      audioWaitInterval: null,
      tabChanged: false,
      isCustomYoutubeSource: false,
    };
  },
  watch: {},
  mounted() {
    videojs.registerPlugin('youtubeSourcePlugin', youtubeSourcePlugin);
    this.create();
  },
  computed: {
    videoOptions() {
      return this.$store.state.video.videoSettings;
    },
    customSize() {
      return this.setSize(this.vid.settings.unmute_size);
    },
    customPlayButton() {
      let primaryColor = 'transparent';
      if(this.vid.play_button && this.vid.play_button.type === 1) {
        primaryColor = this.vid.settings.color.primary;
      }
      return {
        '--playIconSize': this.setPlayButtonSize(this.vid.play_button?.size) ?? '96px',
        '--play-button-primary-color': primaryColor
      };
    },
  },
  created() {
    this.videoAnalytics();
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
    }
    if (this.audioPlayer) {
      this.$store.dispatch('videoCustomSourceId');
      this.audioPlayer.dispose();
    }
    document.removeEventListener('click', this.videoEvents);
    document.removeEventListener('visibilitychange', this.visibilityRefresh);
  },
  methods: {
    setAudioElement() {
      const customSrcType = `video/groovevideo-${this.$store.getters.getVideoCustomSourceId}`;
      this.videoOptions.custom_source_type = customSrcType;
      this.$nextTick(() => {
        if (this.$refs.audioPlayer) {
          this.audioPlayer = videojs(this.$refs.audioPlayer, {muted: this.vid.settings.muted});
          const audioSyncMiddleware = this.setAudioMiddleware(customSrcType);
          videojs.use('video/mp4', audioSyncMiddleware);
        }
      });
    },

    setAudioWithVideo() {
      if (this.audio.src && this.$refs.audioPlayer) {
        this.syncAudioWithVideo();
      }
    },

    async create() {
      const self = this;
      this.showLoader();
      this.vid = await this.options;

      // Mux (type 4) must not fetch HLS segments before play unless it autoplays.
      // videoSettings is a shared store object, so every video sets it explicitly.
      this.videoOptions.preload = hlsPreload(this.vid, Boolean(Number(this.vid.settings && this.vid.settings.autoplay)));

      // if(this.activeIntegrations && this.vid.watched_until) {
      //   this.watchedUntilPoints = this.vid.watched_until;
      // }

      // Setting video source and type
      if( this.vid.public_video_source_type == 1 && typeof this.vid.is_local !== 'undefined' && this.vid.is_local == 1 && typeof this.vid.video_stream_source !== 'undefined' && this.vid.video_stream_source !== '' && this.vid.video_stream_source !== null){
        this.$store.dispatch('videoSrc', this.vid.video_stream_source);
        this.$store.dispatch('videoType', 'application/x-mpegURL');
        /**
         * Override native behaviour of browsers for HLS videos 
         * to make quality selector work 
         * (Except Safari)
         */ 
        this.videoOptions.html5 = {
          vhs: {
            overrideNative: !videojs.browser.IS_SAFARI
          }
        };
      } else if(typeof this.vid.public_video_source !== 'undefined' && this.vid.public_video_source) {
        // const { video, audio } = this.setYoutubeSource(
        //   this.vid.public_video_source
        // );
        // this.videoOptions.sources[0].src = video;
        // this.audio.src = audio;


        // console.log(this.videoOptions.sources, {
        //     src: this.vid.video_source,
        //     type: 'video/youtube',
        //   },);

        // const video = this.setYoutubeLink(this.vid.public_video_source);
        // this.isCustomYoutubeSource = true;

        // this.videoOptions.sources = video;
        // this.videoOptions.sources = [];
        // this.videoOptions.sources.push({
        //   src: this.vid.video_source,
        //   type: 'video/youtube',
        // });

        // this.videoOptions.techOrder = ['html5', 'youtube'];
        this.videoOptions.techOrder = ['youtube', 'html5'];
        this.videoOptions.retryOnError = true;
        this.videoOptions.sources = [
          {
            src: this.vid.video_source,
            type: 'video/youtube',
          },
          {
            src: this.vid.public_video_source,
            type: 'video/mp4',
          },
        ];

      } else {
        this.$store.dispatch('videoSrc', this.vid.temp_video_source ?? this.vid.video_source);
        this.$store.dispatch('videoType', 'video/mp4');
      }

      // set alternative audio
      this.setAudioElement();

      // Setting poster
      if(this.vid.settings.splashImg && this.vid.settings.splashImg != '') {
        this.$store.dispatch('videoPoster', this.vid.settings.splashImg);
      } else if (this.vid.video_source_type == 2) {
        // For YouTube videos, try to get high-quality thumbnail
        const videoId = extractYoutubeVideoId(this.vid.video_source);
        if (videoId) {
          getYoutubeThumbnailUrl(videoId).then(thumbnailUrl => {
            if (thumbnailUrl) {
              this.options.poster = thumbnailUrl;
            } else {
              this.options.poster = this.vid.thumbnail;
            }
          }).catch(() => {
            this.options.poster = this.vid.thumbnail;
          });
        } else {
          this.options.poster = this.vid.thumbnail;
        }
      } else {
        this.$store.dispatch('videoPoster', this.vid.thumbnail_url);
      }
      this.$store.dispatch('videoVideoControl', this.vid.settings.control);
      this.$store.dispatch('videoAutoLoad', this.vid.settings.autoplay);
      // Set muted before constructing the tech, not only in its ready callback.
      // An initial unmuted volume event otherwise dismisses the new sound art.
      this.$store.dispatch('videoMute', usesModernAutoplay(this.vid) ? true : this.vid.settings.muted);
      this.$store.dispatch('videoEndAction', this.vid.end_actions);
      if (this.vid.video_source_type == 2 && this.vid.public_video_source_type == 1) {
        // this.$store.dispatch('videoTechOrder', 'html5');
        // this.$store.dispatch('videoType', 'video/mp4');
        this.$store.dispatch('videoTechOrder', 'youtube');
        this.$store.dispatch('videoType', 'video/youtube');
      } else if (this.vid.video_source_type == 2 && this.vid.public_video_source_type == 2) {
        this.$store.dispatch('videoTechOrder', 'youtube');
        this.$store.dispatch('videoType', 'video/youtube');
        this.videoOptions.youtube = {
          iv_load_policy: 1,
          ytControls: [false, 0].includes(this.vid.settings.ytControls) ? 0 : 2,
          wmode: 'transparent',
          modestbranding: 1,
          rel: 0
        };
      } else if (this.vid.public_video_source_type == 3) {
        this.$store.dispatch('videoTechOrder', 'html5');
        this.$store.dispatch('videoType', 'video/mp4');
      } else {
        this.$store.dispatch('videoTechOrder', 'html5');
        // this.$store.dispatch('videoType', 'video/mp4');
      }

      // Control settings
      if (!this.vid.settings.control) {
        this.videoOptions.controlBar.playToggle = false;
        this.videoOptions.controlBar.remainingTimeDisplay = false;
        this.videoOptions.controlBar.progressControl.seekBar = false;
        this.videoOptions.controlBar.fullscreenToggle = false;
        this.videoOptions.controlBar.pictureInPictureToggle = false;
        this.videoOptions.controlBar.volumePanel = false;
        this.videoOptions.controlBar.muteToggle = false;
      } else {
        this.videoOptions.controlBar.playToggle = true;
        this.videoOptions.controlBar.remainingTimeDisplay = true;
        this.videoOptions.controlBar.progressControl.seekBar = true;
        this.videoOptions.controlBar.fullscreenToggle = true;
        this.videoOptions.controlBar.pictureInPictureToggle = true;
        this.videoOptions.controlBar.volumePanel = true;
      }
      if(this.vid.settings.theme == 'vjs-theme-wistia') {
        this.videoOptions.controlBar.volumePanel = { inline: false };
        this.videoOptions.controlBar.currentTimeDisplay = true;
        this.videoOptions.controlBar.remainingTimeDisplay = false;
      }
      this.player = videojs(
        this.$refs.videoPlayer,
        this.videoOptions,
        function onPlayerReady() {
          /**
           * Auto play with sound is not allowed
           * Muted autoplay is always allowed.
           * https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
           */
          self.setAudioWithVideo();
          
          if (self.videoOptions.autoplay) {
            this.player()?.muted(true);
            this.player()?.play();
          }
          SeekButton(self.player);

          // Load quality selector after player is ready
          this.hlsQualitySelector({
            //  displayCurrentQuality: true,
            vjsIconClass: 'vjs-icon-cog'
          });
        }
      );

      this.player.responsive(true);
      this.player.fluid(true);

      if (this.isCustomYoutubeSource) {
        this.player.youtubeSourcePlugin(this.videoOptions);
      }

      // Apply YouTube iframe styling when it's a YouTube video
      if (this.vid.video_source_type == 2) {
        this.player.ready(() => {
          const ytIframe = this.player.tech(true)?.ytPlayer?.getIframe();
          if (ytIframe && !this.selectedVideoFormat) {
            // Original style for non-fullscreen
            ytIframe.style.height = '200%';
            ytIframe.style.top = '-50%';
            
            const videoAspectRatio = 16 / 9;

            // Get actual video dimensions from store
            // let videoAspectRatio = this.$store.state.video.videoDimension.width / this.$store.state.video.videoDimension.height;
            // console.log('width', this.$store.state.video.videoDimension.width, 'height', this.$store.state.video.videoDimension.height);

            // Throttling function to prevent too many updates
            let resizeTimeout = null;
            const throttledResize = () => {
              if (resizeTimeout === null) {
                resizeTimeout = setTimeout(() => {
                  updateYouTubeLayout();
                  resizeTimeout = null;
                }, 500);
              }
            };
            
            const updateYouTubeLayout = () => {
              if (!this.player.isFullscreen()) return;
              
              const containerHeight = window.innerHeight;
              const containerWidth = window.innerWidth;
              const containerAspectRatio = containerWidth / containerHeight;
              
              if (containerAspectRatio > videoAspectRatio) {
                const newWidth = containerHeight * videoAspectRatio + 'px';
                ytIframe.style.height = '200%';
                ytIframe.style.width = newWidth;
                ytIframe.style.maxWidth = 'none';
                ytIframe.style.top = '-50%';
                ytIframe.style.left = '50%';
                ytIframe.style.transform = 'translateX(-50%)';
              } else {
                ytIframe.style.height = '200%';
                ytIframe.style.width = '100%';
                ytIframe.style.top = '-50%';
                ytIframe.style.left = '0';
                ytIframe.style.transform = 'none';
              }
            };
            
            // Create a ResizeObserver to detect actual size changes
            const resizeObserver = new ResizeObserver(throttledResize);
            
            // Listen for fullscreenchange event
            this.player.on('fullscreenchange', () => {
              if (this.player.isFullscreen()) {
                // Start observing container for size changes
                resizeObserver.observe(this.player.el_);
                
                // Small delay
                setTimeout(updateYouTubeLayout, 50);
                setTimeout(updateYouTubeLayout, 1000);
              } else {
                // Stop observing when exiting fullscreen
                resizeObserver.disconnect();
                
                // Restore original styles when exiting fullscreen
                ytIframe.style.height = '200%';
                ytIframe.style.width = '100%';
                ytIframe.style.top = '-50%';
                ytIframe.style.left = '0';
                ytIframe.style.transform = 'none';
              }
            });
            
            // Also listen for player resize events
            window.addEventListener('resize', throttledResize);
            
            // Clean up when component is destroyed
            this.player.on('dispose', () => {
              resizeObserver.disconnect();
              window.removeEventListener('resize', throttledResize);
              clearTimeout(resizeTimeout);
            });
          }
        });
        this.player.on('ended', () => {
          this.player.currentTime(0);
          this.player.pause();
          this.player.trigger('timeupdate');
          setTimeout(() => {
            this.player.trigger('timeupdate');
          }, 500);
        });
      }

      // video dimension settings
      this.dimensions_type = this.$store.state.video.videoDimension.type;
      this.video_width = this.$store.state.video.videoDimension.width;
      this.video_height = this.$store.state.video.videoDimension.height;

      this.setAspectRatio();
      if(this.player.currentType() === 'video/youtube' && !this.options.autoplay) {
        this.setYouTubeInitialPlay(); // Used In case of Youtube tech      
      }

      // Disable context menu/ right click
      self.player.on('contextmenu', (e) => {
        e.preventDefault();
      });

      const playButton = document.getElementsByClassName("vjs-big-play-button")[0];

      if (this.vid.play_button) {
        if(!this.vid.play_button.enable) {
          playButton.style.display = 'none';
        } else {
          // Display play button on pause
          if(this.vid.play_button.show_on_pause) {
            playButton.classList.add('display-on-pause');
          }

          if(this.vid.play_button.animation !== 'none') {
            playButton.classList.add('animate__infinite', 'animate__slower', `animate__${this.vid.play_button.animation}`, 'animate__animated');
          }
          if(this.videoOptions.autoplay) {
            playButton.style.display = 'block';
          }
        }

        if(this.vid.play_button.type === 2) {
          playButton.setAttribute("id", "customPlayButton");
          const url = require(`../../assets/images/icons/play-buttons/${this.vid.play_button.library_source}`);
          document.getElementsByClassName("vjs-big-play-button")[0].style.backgroundImage = `url("${  url  }")`;
        }
        else if(this.vid.play_button.type === 3) {
          playButton.setAttribute("id", "customPlayButton");
          document.getElementsByClassName("vjs-big-play-button")[0].style.backgroundImage = `url("${  this.vid.play_button.custom_source  }")`;
        }
      }

      let lastClickTime = 0;
      let count = 0;
      self.player.on('touchstart', (e) => {
        // Play/Pause events on touchscreen
        if(e.target && e.target.classList.contains('vjs-tech')) {
          if (self.player.paused()) {
            self.player.play();
          } else {
            self.player.pause();
          }
        } else if (!this.hidePlayButton && e.target && e.target.classList.contains('vjs-icon-placeholder') && this.videoOptions.autoplay) {
          self.removeUnmuteImage();
        }

        // Full screen event on double touchscreen
        const clickTime = Date.now();
        if (clickTime - lastClickTime < 300 && count === 1) {
          if(self.player.isFullscreen()) {
            self.player.exitFullscreen();
          } else {
            self.player.requestFullscreen();
          }
          count = 0;
        } else {
          count = 1;
        }
        lastClickTime = clickTime;
      });

      // No full screen on touchmove event
      self.player.on('touchmove', (e) => { 
        count = 0;
      });

      if (this.dimensions_type == 1) {
        this.player.responsive(true);
      } else {
        this.player.responsive(false);
        this.player.width(this.video_width);
        this.player.height(this.video_height);
      }

      const overlayObjectsArray = [];

      // Adding click for sound
      if(this.videoOptions.autoplay && this.vid.settings.autoplay_settings.show_illustration) {
        this.showUnmuteImage = true;
        let imageSrc;
        if (this.vid.settings.autoplay_settings.type === 2) {
          imageSrc = this.vid.settings.autoplay_settings.custom_image;
        } else if (this.vid.settings.autoplay_settings.type === 1) {
          const unmuteImage = this.vid.settings.unmute_image ? this.vid.settings.unmute_image : 'ClickForSound_1.png';
          imageSrc = require(`../../assets/images/videos/unmute-video/${unmuteImage}`);
        }
        
        let imageClasses = 'autoplay-unmute-image cursor-pointer';
        if (this.vid.settings.unmute_animation && this.vid.settings.unmute_animation !== 'none') {
          this.showAnimation = true;
          imageClasses += ` animate__infinite animate__slower animate__${this.vid.settings.unmute_animation}`;
        }

        overlayObjectsArray.push({
          start: 0,
          end: 'hide_unmute_image',
          align: 'top-left',
          class: 'unmute-image-overlay',
          content: `<img src="${imageSrc}" class="${imageClasses}" style="--iconSize:${this.customSize}" id="clickForSoundImage"/>`,
        });
      }

      // Hide the click for sound image when video is unmuted
      if(this.showUnmuteImage) {
        self.player.on('volumechange', (e) => {
          if (usesModernAutoplay(this.vid) && !self.player.hasStarted()) return;
          if(this.showUnmuteImage && !self.player?.muted()) {
            self.hideUnmuteImage();
          }
        });
      }

      // Animation start/stop on play/pause
      if(self.videoOptions.autoplay) {
        self.player.on('play', (e) => {
          if(this.hidePlayButton && this.vid.play_button.show_on_pause) {
            self.player?.bigPlayButton.hide();
          }
          if(self.showAnimation) {
            const animationClasses = document.getElementById('clickForSoundImage').classList;
            if(!animationClasses.contains('animate__animated')) {
              animationClasses.add('animate__animated');
            }
          }
        });

        self.player.on('pause', (e) => {
          if(this.hidePlayButton && this.vid.play_button.show_on_pause) {
            self.player?.bigPlayButton.show();
          }
          if(self.showAnimation) {
            const animationClasses = document.getElementById('clickForSoundImage').classList;
            if(animationClasses.contains('animate__animated')) {
              animationClasses.remove('animate__animated');
            }
          }
        });
      }

      const watermark =
        typeof this.vid.settings.watermark !== 'undefined' &&
        typeof this.vid.settings.watermark.source !== 'undefined' &&
        this.vid.settings.watermark.source != ''
          ? this.vid.settings.watermark.source
          : '';
      const watermarkPosition =
        typeof this.vid.settings.watermark !== 'undefined' &&
        typeof this.vid.settings.watermark.position !== 'undefined' &&
        this.vid.settings.watermark.position != ''
          ? this.vid.settings.watermark.position
          : 'top-right';

      if (watermark && watermarkPosition) {
        overlayObjectsArray.push({
          start: 0,
          end: 86400,
          align: watermarkPosition,
          class: 'watermark-overlay',
          content: `<img src="${  watermark  }" />`,
        });
      }

      // Adding overlays
      if (
        this.vid.settings.overlay && 
        (this.vid.settings.overlay.enable == true ||
          this.vid.settings.overlay.enable == 'true')
      ) {
        const videoText = this.$store.state.video.videoOverlayText
          ? this.$store.state.video.videoOverlayText
          : this.vid.settings.overlay.txt;

        if (videoText && videoText !== '') {
          overlayObjectsArray.push({
            start: 0,
            end: 86400,
            align: 'top-left',
            class: 'fullwidth-overlay',
            content: videoText,
          });
        }
      }

      // step3 overaly settings
      const controlsSettings = this.$store.state.video.videoSettings.showControls;
      if (this.vid.call_to_actions.length > 0) {
        this.vid.call_to_actions.forEach((item) => {
          // optin Form
            if (item.cta.cta_form == true) {
            this.triggerWatchedUntil = true;
            const formId = item.cta.index;
            let lastSecond = null;
            var secondsToTriggerEvent = item.cta.seconds;
            this.overlayTimings.push({
              trigger: `optin_${formId}_activated`,
              time: secondsToTriggerEvent
            });

            self.player.on('timeupdate', () => {
              const seconds = Math.floor(self.player.currentTime());
              if (seconds == item.cta.seconds && lastSecond !== seconds) {
                lastSecond = seconds;
                self.player.pause();
                self.player.trigger(`optin_${  formId  }_activated`);
                if (
                  self.videoOptions.showControls &&
                  (self.videoOptions.showControls == 1 ||
                    self.videoOptions.showControls == '1' ||
                    self.videoOptions.showControls == true ||
                    self.videoOptions.showControls == 'true')
                ) {
                  self.player.controls(false);
                }
              }
            });

            if (item.cta.show_skip_button) {
              const formContent =
                `
                  <div>
                    <div class="optin-form-cta-contaner flex flex-col items-center text-center">
                      <div class="optin-form-cta-heading">
                        ${item.cta.title.charAt(0).toUpperCase() + item.cta.title.slice(1)}
                      </div>
                      <input id="optin_name_${formId}" class="cta-optin-form-field" type='text' name='' placeholder='Name'>
                      <input id="optin_email_${formId}" class="cta-optin-form-field cta-optin-form-field-email" type='text' name='' placeholder='Email'>
                      <button data-form-id="${formId}" class="cta-optin-form-submit-button">
                        Submit
                      </button>
                    </div>
                    <div class="cta-skip-button cta-skip-optin-button optin-skip" data-form-id="${formId}">
                      Skip
                    </div>
                  </div>
                `;

              overlayObjectsArray.push({
                start: `optin_${  formId  }_activated`,
                end: `optin_${  formId  }_deactivated`,
                align: 'top-left',
                class: 'fullwidth-height-video-overlay',
                content: formContent,
              });
            } else {
              const formContent =
                `
                  <div class="optin-form-cta-contaner flex flex-col items-center text-center">
                    <div class="optin-form-cta-heading">
                      ${item.cta.title.charAt(0).toUpperCase() + item.cta.title.slice(1)}
                    </div>
                    <input id="optin_name_${formId}" class="cta-optin-form-field" type='text' name='' placeholder='Name'>
                    <input id="optin_email_${formId}" class="cta-optin-form-field cta-optin-form-field-email" type='text' name='' placeholder='Email'>
                    <button data-form-id="${formId}" class="cta-optin-form-submit-button">
                      Submit
                    </button>
                  </div>
                `;

              overlayObjectsArray.push({
                start: `optin_${  formId  }_activated`,
                end: `optin_${  formId  }_deactivated`,
                align: 'top-left',
                class: 'fullwidth-height-video-overlay',
                content: formContent,
              });
            }
          }

          // Banner
          if (item.cta.cta_banner == true) {
            const bannerId = self.rand(1000, 9999);
            let lastSecond = null;
            var secondsToTriggerEvent = item.cta.seconds;
            this.overlayTimings.push({
              trigger: `banner_${bannerId}_activated`,
              time: secondsToTriggerEvent
            });

            self.player.on('timeupdate', () => {
              const seconds = Math.floor(self.player.currentTime());
              if (seconds == item.cta.seconds && lastSecond !== seconds) {
                lastSecond = seconds;
                self.player.pause();
                self.player.trigger(`banner_${  bannerId  }_activated`);
                if (
                  self.videoOptions.showControls &&
                  (self.videoOptions.showControls == 1 ||
                    self.videoOptions.showControls == '1' ||
                    self.videoOptions.showControls == true ||
                    self.videoOptions.showControls == 'true')
                ) {
                  self.player.controls(false);
                }
              }
            });

            const bannerLink = item.cta.url;
            const bannerTarget = item.cta.new_tab_toggle != undefined && item.cta.new_tab_toggle === false ? '_self' : '_blank';

            let captionButtonContent = `<div class="banner-cta-contaner">
                                            <div class="banner-cta-banner" data-banner-id="${bannerId}" data-banner-link="${bannerLink}" data-banner-target="${bannerTarget}" style="background-image: url('${item.cta.imgName}');">
                                            </div>
                                        </div>`;

            if (item.cta.show_skip_button) {
               captionButtonContent = `<div>
                                          ${captionButtonContent}
                                          <div class="cta-skip-button cta-skip-banner-button banner-skip" data-banner-id="${bannerId}">
                                              Skip <i class="mr-2 ml-1 text-xs fas fa-chevron-right" aria-hidden="true"></i>
                                          </div>
                                        </div>`;
            }

            overlayObjectsArray.push({
              start: `banner_${  bannerId  }_activated`,
              end: `banner_${  bannerId  }_deactivated`,
              align: 'top-left',
              class: 'fullwidth-height-video-overlay',
              content: captionButtonContent,
            });
            
          }

          // Caption
          if (item.cta.cta_caption == true) {
            let overlayContent = item.cta.title;

            if (item.cta.is_link_activated) {
              const overlayTarget = item.cta.new_tab_toggle != undefined && item.cta.new_tab_toggle === false ? '_self' : '_blank';
              overlayContent = `<a target="${overlayTarget}" href="${item.cta.cta_caption_url}">${item.cta.title}</a>`;
            }
            
            overlayObjectsArray.push({
              start: item.cta.seconds,
              end: item.cta.till_seconds,
              align: 'bottom-left',
              class: 'fullwidth-caption-overlay',
              content: overlayContent,
            });
          }

          // Caption with button
          if (item.cta.cta_caption_button == true) {
            let target = '_blank';
            if(item.cta.new_tab_toggle != undefined && item.cta.new_tab_toggle === false) {
              target = '_self';
            }
            const captionButtonContent = `<div class="caption-cta-button-container cta-overlay">
              <div style="width: 75%" class="caption-cta-button-title">
                  ${item.cta.heading}
              </div>
              <div style="width: 25%">
                  <div class="caption-cta-caption-button">
                      <a href="${item.cta.url}" target="${target}">
                          ${item.cta.button_text}
                      </a>
                  </div>
              </div>
          </div>`;

            overlayObjectsArray.push({
              start: item.cta.seconds,
              end: item.cta.till_seconds,
              align: 'bottom-left',
              class: 'fullwidth-caption-overlay',
              content: captionButtonContent,
            });
          }

          // end
        });

        // WatchedUntil
        // if(this.watchedUntilPoints.length>0 && this.triggerWatchedUntil) {
        //   this.watchedUntilPoints.forEach((item) => {
        //     const pointId = self.rand(1000, 9999);
        //     let lastSecond = null;
        //     var secondsToTriggerEvent = item.minutes *60;

        //     self.player.on('timeupdate', () => {
        //       const seconds = Math.floor(self.player.currentTime());
        //       if (seconds == secondsToTriggerEvent && lastSecond !== seconds) {
        //         lastSecond = seconds;
        //         self.triggerIntegration('watched',item.id, item.optin_id);
        //       }
        //     });
        //   });
        // }
      }

      // setting the overlayvalue on video
      if (overlayObjectsArray.length > 0) {
        self.player.overlay({
          overlays: overlayObjectsArray,
        });
      }

      const formSubmissionURL = `${window.config.apiUrl  }/optinForm`;
      document.addEventListener('click', this.videoEvents = (e) => {
        if(e.target && (e.target.classList.contains('autoplay-unmute-image') || (!this.hidePlayButton && e.target.classList.contains('vjs-icon-placeholder') && this.videoOptions.autoplay))) {
          this.removeUnmuteImage();
        } else if (e.target && e.target.classList.contains('banner-cta-banner')) {
          const bannerId = e.target.getAttribute('data-banner-id');
          const bannerLink = e.target.getAttribute('data-banner-link');
          const bannerTarget = e.target.getAttribute('data-banner-target');

          window.open(bannerLink, bannerTarget);

          self.player.trigger(`banner_${  bannerId  }_deactivated`);
          self.player.play();
          if (
            self.videoOptions.showControls &&
            (self.videoOptions.showControls == 1 ||
              self.videoOptions.showControls == '1' ||
              self.videoOptions.showControls == true ||
              self.videoOptions.showControls == 'true')
          ) {
            self.player.controls(true);
          }
        } else if (
          e.target &&
          e.target.classList.contains('cta-optin-form-submit-button')
        ) {
          const videoId = self.vid.id;
          const formId = e.target.getAttribute('data-form-id');
          const name = document.querySelector(`#optin_name_${  formId}`).value;

          const email = document.querySelector(`#optin_email_${  formId}`).value;

          function validateEmail(email) {
            const re = /\S+@\S+\.\S+/;
            return re.test(email);
          }

          if (validateEmail(email) == true && name !== '') {
            const xhttp = new XMLHttpRequest();
            xhttp.onreadystatechange = function () {
              if (this.readyState == 4 && this.status == 200) {
                self.optinDetails.push({
                  id: JSON.parse(xhttp.response)[0].id,
                  optin: formId,
                });
                self.player.trigger(`optin_${  formId  }_deactivated`);
                self.player.play();
                if (
                  self.videoOptions.showControls &&
                  (self.videoOptions.showControls == 1 ||
                    self.videoOptions.showControls == '1' ||
                    self.videoOptions.showControls == true ||
                    self.videoOptions.showControls == 'true')
                ) {
                  self.player.controls(true);
                }
              }
            };
            xhttp.open('POST', formSubmissionURL, true);
            xhttp.setRequestHeader(
              'Content-type',
              'application/x-www-form-urlencoded'
            );

            xhttp.send(
              `name=${ 
                name 
                }&email=${ 
                email 
                }&vid=${ 
                videoId 
                }&count=${ 
                1}&integrations=${
                // this.activeIntegrations
                false
                }&optin=${
                formId
                }`
            );
          }
        } else if (
          e.target &&
          e.target.classList.contains('cta-skip-banner-button')
        ) {
          const bannerId = e.target.getAttribute('data-banner-id');
          self.player.trigger(`banner_${  bannerId  }_deactivated`);
          self.player.play();
          if (
            self.videoOptions.showControls &&
            (self.videoOptions.showControls == 1 ||
              self.videoOptions.showControls == '1' ||
              self.videoOptions.showControls == true ||
              self.videoOptions.showControls == 'true')
          ) {
            self.player.controls(true);
          }
        } else if (
          e.target &&
          e.target.classList.contains('cta-skip-optin-button')
        ) {
          const formId = e.target.getAttribute('data-form-id');
          self.player.trigger(`optin_${  formId  }_deactivated`);
          self.player.play();
          if (
            self.videoOptions.showControls &&
            (self.videoOptions.showControls == 1 ||
              self.videoOptions.showControls == '1' ||
              self.videoOptions.showControls == true ||
              self.videoOptions.showControls == 'true')
          ) {
            self.player.controls(true);
          }
        }
      });

      if (
        self.videoOptions.showControls &&
        (self.videoOptions.showControls == 1 ||
          self.videoOptions.showControls == '1' ||
          self.videoOptions.showControls == true ||
          self.videoOptions.showControls == 'true')
      ) {
      } else {
        self.player.controlBar.progressControl.disable();
        self.player.addClass('hide-control-bar');
      }

      // palybacksetting
      const createNewRanges = (timeRanges, playbackRate) => {
        const newRanges = [];

        for (let i = 0; i < timeRanges.length; i++) {
          newRanges.push([
            timeRanges.start(i) / playbackRate,
            timeRanges.end(i) / playbackRate,
          ]);
        }

        return videojs.createTimeRange(newRanges);
      };

      // Adjust values based on playback rate
      const playbackrateAdjuster = function (player) {
        let tech;

        // Listen for ratechange event
        player.on('ratechange', () => {
          tech.trigger('durationchange');
          tech.trigger('timeupdate');
        });

        // Implement setSource() to work with all sources
        return {
          setSource(srcObj, next) {
            next(null, srcObj);
          },

          // Store the tech that Video.js gives us after source selection is complete.

          setTech(newTech) {
            tech = newTech;
          },

          // Override methods
          duration(dur) {
            return dur / player.playbackRate();
          },

          currentTime(ct) {
            return ct / player.playbackRate();
          },

          setCurrentTime(ct) {
            return ct * player.playbackRate();
          },

          buffered(bf) {
            return createNewRanges(bf, player.playbackRate());
          },

          seekable(seekable) {
            return createNewRanges(seekable, player.playbackRate());
          },

          played(played) {
            return createNewRanges(played, player.playbackRate());
          },
        };
      };

      // +++ Register the middleware with the player +++
      videojs.use('*', playbackrateAdjuster);

      videojs.getPlayer('#videojs-event-tracking-player').ready(function () {
        // When the player is ready, get a reference to it
        const myPlayer = this;
        // +++ Define the playback rate options +++
        const options = { playbackRates: [0.5, 1, 1.25, 1.5, 1.75, 2] };
        // +++ Initialize the playback rate button +++
        myPlayer.controlBar.playbackRateMenuButton =
          myPlayer.controlBar.addChild('PlaybackRateMenuButton', {
            playbackRates: options.playbackRates,
          });
      });

      document.addEventListener('visibilitychange', this.visibilityRefresh = (event) => {
        if (this.audioPlayer && document.visibilityState === 'visible') {
          console.log('visible');
          this.refreshTimeUpdate();
        } else if (this.audioPlayer && !this.audioPlayer.paused()) {
          this.player.play();
          this.tabChanged = true;
        }
      });
      // end palybacksetting

      this.hideLoader();
    },

    setSize(iconSize) {
      let size = '96px';
      if(iconSize === 1) {
        size = '32px';
      } else if(iconSize === 2) {
        size = '48px';
      } else if(iconSize === 3) {
        size = '72px';
      }
      return size;
    },

    setPlayButtonSize(iconSize) {
      let size = '96px';
      if(iconSize === 1) {
        size = '72px';
      } else if(iconSize === 3) {
        size = '120px';
      } else if(iconSize === 4) {
        size = '144px';
      }
      return size;
    },

    hideUnmuteImage() {
      this.showUnmuteImage = false;
      this.showAnimation = false;
      this.hidePlayButton = true;
      if (this.audioPlayer) {
        this.audioPlayer.play();
      }
      if(this.vid.end_actions !== '2') {
        this.player?.loop(false);
      }
      this.player.trigger('hide_unmute_image');
      const img = document.getElementById('clickForSoundImage');
      if(document.contains(img)) {
        img.remove();
      }
      this.player?.bigPlayButton.hide();
    },

    removeUnmuteImage() {
      if(this.vid.settings.autoplay_settings.begin_again) {
        this.player.currentTime(0);
      }
      this.player?.muted(false);

      this.hideUnmuteImage();
    },

    setAspectRatio() {
      if(!this.vid.dimension_percentage && (this.vid.video_source_type !== 2 || this.vid.public_video_source_type !== 2)) {
        const self = this;

        this.player.on('loadedmetadata', function() {
          console.log('width------------------------', this.videoWidth(), this.videoHeight());
          const width = this.videoWidth();
          const height = this.videoHeight();

          if(width > 0 && height > 0) {
            const videoPadding = height/width * 100;
            axios.put(`video/${self.vid.id}/${self.vid.permalinks}/set-dimension`, {
              dimension_percentage: videoPadding,
              width,
              height
            });
          }
        });
      }
    },
    
    rand(min, max) {
      const randomNum = Math.random() * (max - min) + min;
      return Math.round(randomNum);
    },

    async videoAnalytics() {},

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style>
.video-internal-wrapper {
  width: 100%;
  max-height: 500px;
  display: flex;
  align-items: center;
}

.video-js{
  overflow: hidden !important;
}
</style>
